import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { 
  ArrowRight,
  Phone,
  Target,
  Cloud,
  TrendingUp,
  Settings,
  Shield,
  Server
} from 'lucide-react';

const Services = () => {

  const leistungen = [
    {
      icon: Cloud,
      title: 'Microsoft 365 Optimierung',
      url: '/microsoft-365-optimierung',
      problem: 'Sie zahlen für Microsoft 365, nutzen aber nur E-Mail und OneDrive?',
      loesung: 'Ich zeige Ihnen, wie Sie Teams, SharePoint und andere Tools gewinnbringend einsetzen – ohne unnötige Komplexität.',
      ergebnis: 'Bessere Zusammenarbeit, weniger E-Mail-Chaos, mehr Überblick.'
    },
    {
      icon: TrendingUp,
      title: 'Digitalisierung & Prozessoptimierung',
      url: '/digitalisierung-prozessoptimierung',
      problem: 'Viele manuelle Arbeitsschritte, unklare Abläufe, verschwendete Zeit?',
      loesung: 'Ich helfe Ihnen, Arbeitsabläufe Schritt für Schritt zu digitalisieren – dort, wo es sich wirklich lohnt.',
      ergebnis: 'Weniger Aufwand, weniger Fehler, mehr Zeit fürs Wesentliche.'
    },
    {
      icon: Settings,
      title: 'IT-Infrastruktur für KMU',
      url: '/it-infrastruktur-kmu',
      problem: 'Ihre IT läuft irgendwie, aber niemand weiß so richtig, was da eigentlich passiert?',
      loesung: 'Ich bringe Struktur in Ihre IT – Server, Netzwerk, Backups. Verständlich dokumentiert.',
      ergebnis: 'Eine IT, die funktioniert. Und auf die Sie sich verlassen können.'
    },
    {
      icon: Shield,
      title: 'IT-Sicherheit & ISMS-Grundlagen',
      url: '/it-sicherheit-isms',
      problem: 'Unsicher bei Datenschutz und IT-Sicherheit? Angst vor Cyberangriffen?',
      loesung: 'Ich zeige Ihnen, welche Sicherheitsmaßnahmen wirklich wichtig sind – realistisch und umsetzbar.',
      ergebnis: 'Grundlegende Sicherheit, klare Regeln, besseres Gefühl.'
    },
    {
      icon: Server,
      title: 'Virtualisierung & Linux-Administration',
      url: '/virtualisierung-linux',
      problem: 'Teure Hardware, unflexible Serverumgebungen, kein Know-how für Linux?',
      loesung: 'Ich helfe bei Virtualisierung (Proxmox, VMware, Hyper-V), Docker-Containern und Linux-Servern – praxisnah und ohne Overhead.',
      ergebnis: 'Flexible Infrastruktur, geringere Kosten, weniger Abhängigkeit von einem Betriebssystem.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="IT-Leistungen für KMU in Mönchengladbach – Schteffann IT-Dienstleistungen"
        description="Microsoft 365 Optimierung, Digitalisierung, IT-Infrastruktur, IT-Sicherheit & Virtualisierung für KMU in Mönchengladbach. Pragmatisch, verständlich, ehrlich."
        keywords="IT-Dienstleistungen Mönchengladbach, Microsoft 365 Optimierung, Digitalisierung KMU, IT-Infrastruktur, IT-Sicherheit, Virtualisierung Linux, IT-Support Mönchengladbach, Viersen, Krefeld, NRW"
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              Leistungen
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">IT-Leistungen für KMU in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Pragmatische IT-Lösungen für Unternehmen in Mönchengladbach, Viersen und Krefeld. Kein Marketing-Blabla, keine unnötigen Projekte.
            </p>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20">
        <div className="container">
          <div className="space-y-12">
            {leistungen.map((item, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-cyan-200 overflow-hidden">
                <CardContent className="p-8 lg:p-12">
                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                        <item.icon className="h-10 w-10 text-cyan-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-6">
                        {item.title}
                      </h2>
                      
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Problem</h3>
                          <p className="text-lg text-slate-700">{item.problem}</p>
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Lösung</h3>
                          <p className="text-lg text-slate-700">{item.loesung}</p>
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Ergebnis</h3>
                          <p className="text-lg text-slate-900 font-medium">{item.ergebnis}</p>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <Link to={item.url}>
                          <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white">
                            Details ansehen
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Welche Leistung passt zu Ihnen?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns darüber sprechen, was für Ihr Unternehmen am meisten Sinn macht.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Erstgespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Services;